import React, { useEffect, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { CheckCircle, Loader2 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const Success = () => {
  const { t } = useLanguage();
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [orderDetails, setOrderDetails] = useState(null);

  useEffect(() => {
    // Get session_id from URL
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');

    if (!sessionId) {
      setStatus('error');
      return;
    }

    const API_URL = process.env.REACT_APP_BACKEND_URL;
    const eventSource = new EventSource(
      `${API_URL}/api/checkout/wait/${sessionId}`
    );

    // Événement "paid" : le webhook Stripe a confirmé le paiement
    eventSource.addEventListener('paid', (event) => {
      try {
        const data = JSON.parse(event.data);
        setOrderDetails({
          amount: Number(data.amount) || 0,
          currency: (data.currency || 'usd').toUpperCase()
        });
        setStatus('success');
      } catch (e) {
        console.error('Error parsing paid event data:', e);
        setStatus('success');
      } finally {
        eventSource.close();
      }
    });

    // Événement "timeout" : aucune confirmation après 120s
    eventSource.addEventListener('timeout', () => {
      setStatus('error');
      eventSource.close();
    });

    // Événement "error" côté backend
    eventSource.addEventListener('error', () => {
      // onerror se déclenche aussi en cas de perte de connexion réseau
      if (eventSource.readyState === EventSource.CLOSED) {
        setStatus((prev) => (prev === 'success' ? prev : 'error'));
      }
    });

    // onerror natif (perte réseau, CORS, etc.)
    eventSource.onerror = () => {
      if (eventSource.readyState === EventSource.CLOSED) {
        setStatus((prev) => (prev === 'success' ? prev : 'error'));
      }
    };

    // Cleanup : fermer la connexion SSE quand le composant est démonté
    return () => {
      eventSource.close();
    };
  }, []);

  return (
    <div className="min-h-screen section-wrapper flex items-center justify-center px-4">
      <Card className="max-w-md w-full" data-testid="success-card">
        <CardContent className="p-8">
          {status === 'checking' && (
            <div className="text-center" data-testid="payment-checking">
              <Loader2 className="w-16 h-16 mx-auto text-purple-600 animate-spin mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Checking Payment Status...
              </h2>
              <p className="text-gray-600">
                Please wait while we confirm your payment
              </p>
            </div>
          )}

          {status === 'success' && (
            <div className="text-center" data-testid="payment-success">
              <CheckCircle className="w-16 h-16 mx-auto text-green-600 mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Payment Successful!
              </h2>
              <p className="text-gray-600 mb-6">
                Thank you for your order. Your custom song will be created and delivered to your email.
              </p>
              {orderDetails && (
                <div className="bg-purple-50 rounded-lg p-4 mb-6">
                  <p className="text-sm text-gray-700">
                    <strong>Amount Paid:</strong> ${orderDetails.amount.toFixed(2)} {orderDetails.currency}
                  </p>
                </div>
              )}
              <a
                href="/"
                data-testid="success-back-home-link"
                className="inline-block bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition-colors"
              >
                Back to Home
              </a>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center" data-testid="payment-error">
              <div className="w-16 h-16 mx-auto bg-red-100 rounded-full flex items-center justify-center mb-4">
                <span className="text-3xl">❌</span>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Payment Error
              </h2>
              <p className="text-gray-600 mb-6">
                Something went wrong with your payment. Please try again or contact support.
              </p>
              <a
                href="/"
                data-testid="error-back-home-link"
                className="inline-block bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition-colors"
              >
                Back to Home
              </a>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Success;
